import React, { useMemo } from 'react';
import styled from 'styled-components';
import { Arr } from '../../../CodesForFoundation';
import { useObserver } from '../../../CodesThatsGottaGoSomewhereElse';
import { LFC } from '../../../Legacy';
import { CommandDispatcher } from '../../../concepts/commands/CommandDispatcher';
import { Colors, Float, Prompt } from '../../../ui';
import { BetterPopArrow } from '../../../ui/PopArrow';
import { useResolve } from '../../Container';
import { Participant } from '../../canvas/participants/Participant';
import { ParticipantManager } from '../../canvas/participants/ParticipantManager';
import { useComponentObserver } from '../../canvas/visualization/ComponentObserver';
import { PopoutWidget } from '../../canvas/workspace/widgets/WidgetInterface';
import { Flexer } from '../../dashboard/elements/LayoutElements';
import { Votable } from './Votable';
import { Vote } from './Vote';
import { votesClose } from './VotingCommands';

const Mount = styled.div`
    position: relative;
    height: 0;
    white-space: nowrap;

    & > * {
        position: absolute;
        transform: translate(-50%, -100%) translateY(-1rem);
    }
`;

const Frame: LFC = ({ children }) => {
    return <Float inline shadow="xl">
        <Flexer flex="column" gap="1" border={`solid 2px ${Colors.mystery}`} borderRadius={.375} padding="1">
            {children}
        </Flexer>
        <BetterPopArrow placement="top" color="#fff" border={[2, Colors.mystery]} size="1.5" style={{ left: '50%' }} />
    </Float>;
};

const Name = styled.span`
    display: inline-block;
    margin-right: .5rem;
`;

const Value = styled.span`
    font-weight: 500;
`;

const computeSummary = (votes: Vote[], ptpts: Participant[]) => {
    const grouped = Arr.groupBy(votes, 'userId');
    const paired = Object.entries(grouped).map(([userId, items]) => {
        const ptpt = userId === null ? Participant.unknown : ptpts.find(p => p.id === userId) ?? null;
        const count = items.length;
        return { key: userId ?? 'anon', name: ptpt?.name || 'Anonymous User(s)', count };
    });
    return Arr.sortBy(paired, 'count').reverse();
};

export const VoteSummaryWidget: LFC<{ widget: PopoutWidget }> = ({ widget }) => {
    const subject = widget.subject as Votable;
    // const color = darken(.2, subject.color);
    const dispatch = useResolve(CommandDispatcher);
    const onClose = () => dispatch(votesClose);

    const state = useComponentObserver(subject);
    const ptpts = useObserver(useResolve(ParticipantManager));
    const summary = useMemo(() => computeSummary(subject.polarize(Vote), ptpts), [state, ptpts]);

    return <Prompt ignoreEnter onAction={onClose}>
        <Mount>
            <Frame>
                <table>
                    <tbody>
                        {summary.map(({ key, name, count }) =>
                            <tr key={key}>
                                <td><Name>{name}:</Name></td>
                                <td><Value>{count}</Value></td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </Frame>
        </Mount>
    </Prompt>;
};
